import { injectable, interfaces } from 'inversify';
import { makeObservable, observable } from 'mobx';
import { nameOf } from '../../../../__legacy__/helpers/object.helpers';
import { SortingDirection } from '../../../../__legacy__/models/sortingDirection.model';
import { IAnnotationToolsStats } from './annotationTools/models/AnnotationToolsStats';
import { IGeneralStats, IStatsBase } from './general/models/GeneralStats';
import { IWorkPerformanceStats } from './workPerformance/models/WorkPerformance';

export interface IProjectStatsStore {
  readonly generalStats: IGeneralStats[];
  readonly generalStatsOrderBy: string;
  readonly generalStatsOrderDirection: SortingDirection | undefined;
  readonly annotationToolsStats: IAnnotationToolsStats[];
  readonly annotationToolsStatsOrderBy: string;
  readonly annotationToolsStatsOrderDirection: SortingDirection | undefined;
  readonly workPerformanceStats: IWorkPerformanceStats[];
  readonly workPerformanceStatsOrderBy: string;
  readonly workPerformanceStatsOrderDirection: SortingDirection | undefined;
  readonly usersEmails: string[];
  readonly isLoading: boolean;
  setAnnotationToolsStats(annotationToolsStats: IAnnotationToolsStats[]): void;
  setAnnotationToolsStatsSorting(orderBy: string, orderDirection: SortingDirection): void;
  setGeneralStats(generalStats: IGeneralStats[]): void;
  setGeneralStatsSorting(orderBy: string, orderDirection: SortingDirection): void;
  setWorkPerformanceStats(workPerformanceStats: IWorkPerformanceStats[]): void;
  setWorkPerformanceStatsSorting(orderBy: string, orderDirection: SortingDirection): void;
  setIsLoading(isLoading: boolean): void;
}

export namespace IProjectStatsStore {
  export const $: interfaces.ServiceIdentifier<IProjectStatsStore> = Symbol('PROJECT_STATS_STORE');
}

export const defaultSortingBy: string = nameOf<IStatsBase>('email');
export const defaultSortingDirection: SortingDirection = SortingDirection.ASC;

@injectable()
export class ProjectStatsStore implements IProjectStatsStore {
  generalStats: IGeneralStats[] = [];

  generalStatsOrderBy: string = defaultSortingBy;

  generalStatsOrderDirection: SortingDirection | undefined = defaultSortingDirection;

  annotationToolsStats: IAnnotationToolsStats[] = [];

  annotationToolsStatsOrderBy: string = defaultSortingBy;

  annotationToolsStatsOrderDirection: SortingDirection | undefined = defaultSortingDirection;

  workPerformanceStats: IWorkPerformanceStats[] = [];

  workPerformanceStatsOrderBy: string = defaultSortingBy;

  workPerformanceStatsOrderDirection: SortingDirection | undefined = defaultSortingDirection;

  isLoading: boolean = false;

  constructor() {
    makeObservable(this, {
      generalStats: observable,
      generalStatsOrderBy: observable,
      generalStatsOrderDirection: observable,
      annotationToolsStats: observable,
      annotationToolsStatsOrderBy: observable,
      annotationToolsStatsOrderDirection: observable,
      workPerformanceStats: observable,
      workPerformanceStatsOrderBy: observable,
      workPerformanceStatsOrderDirection: observable,
      isLoading: observable,
    });
  }

  setGeneralStats(generalStats: IGeneralStats[]): void {
    generalStats.forEach((s) => s.dailyGeneralStats.sort((a, b) => (a.day > b.day ? 1 : -1)));
    this.generalStats = generalStats;
  }

  setAnnotationToolsStats(annotationToolsStats: IAnnotationToolsStats[]): void {
    this.annotationToolsStats = annotationToolsStats;
  }

  setWorkPerformanceStats(workPerformanceStats: IWorkPerformanceStats[]): void {
    this.workPerformanceStats = workPerformanceStats;
  }

  setGeneralStatsSorting(orderBy: string, orderDirection: SortingDirection): void {
    this.generalStatsOrderBy = orderBy;
    this.generalStatsOrderDirection = orderDirection;
  }

  setAnnotationToolsStatsSorting(orderBy: string, orderDirection: SortingDirection): void {
    this.annotationToolsStatsOrderBy = orderBy;
    this.annotationToolsStatsOrderDirection = orderDirection;
  }

  setWorkPerformanceStatsSorting(orderBy: string, orderDirection: SortingDirection): void {
    this.workPerformanceStatsOrderBy = orderBy;
    this.workPerformanceStatsOrderDirection = orderDirection;
  }

  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  get usersEmails(): string[] {
    return this.generalStats.map((gs) => gs.email);
  }
}
